import { IAnalyticsConfiguration } from '../../domain/analytics/analytics-configuration'
import { IDrivingConfiguration, IPageConfiguration } from '../../domain/conf/page'
import { logger, loggerTimeLog, TimerLabels } from '../../utils/common/logger'
import { DOMUtils } from '../../utils/dom'
import { SimpleEvents } from '../../utils/events'
import { GlobalStore } from '../../utils/storage/global'
import { Store } from '../../utils/storage/keys'
import { LocalStore } from '../../utils/storage/local'
import { PageAgeGateService } from '../age-gate/page'
import { IKlangooSensitive } from '../common/nlp/klangoo-config'
import { IKlangooNegativeKeywords } from '../common/nlp/klangoo-config'
import { PageType } from '../common/page-types'
import { IProviderConfig } from '../common/provider-config'
import { Ad, IAd } from './ad'
import { IAdRefreshConf } from './ad'
import { AdEvents, IAdSlotsAddedEvent } from './ad/events'
import {
    IAdBiddingProviderConfig,
    IGptAdapter,
    ITagManagerProviderConfig
} from './adapters'
import { AdURIFormatter } from './formatters/uri'
import { IAdTargetStrategyHandler } from './targeting/strategy'
import { AdTarget } from './targeting/target'

export interface IAdServiceConfig {
    // configs
    bidding?: IAdBiddingProviderConfig[]
    provider?: IProviderConfig
    refresh?: boolean | IAdRefreshConf
    tagManagers?: ITagManagerProviderConfig[]
    verifier?: IProviderConfig

    // subscription-related (for ux)
    adLight?: boolean

    // page meta (for targeting)
    ancestry?: string[]
    articleId?: string
    writers?: IPageConfiguration['writers']
    network: string
    originId?: string
    wpLegacyId?: string
    page?: PageType
    property: string
    sensitive?: IKlangooSensitive
    negative?: IKlangooNegativeKeywords[]
    siteId: string

    analyticsConfig?: IAnalyticsConfiguration
    pageConfig?: IPageConfiguration
}

export interface IAdService {
    initialize(conf: IAdServiceConfig): void

    update(isSubscriber: boolean): Promise<void>
}

export interface IIasScores {
    brandSafety?: { key: string, value: string }
    custom?: { key: string, value: string }
    fr?: string
}

export interface IAdTargetingConfig {
    adUnit?: string
    adTargetingParams?: string
}

export class AdService extends SimpleEvents implements IAdService {
    /**
     * Required DOM selectors and attributes.
     *
     * IMPORTANT: These will be duplicated elsewhere (templates, css) so ensure changes are coordinated
     * and instances minimized.
     */
    public static DOM = {
        attrs: {
            sticky: 'data-ad-sticky', // main selector for stickyness; size array optional for which behaviour
            // applies(default is all)
            stickyOffset: 'data-ad-sticky-offset', // px offset from top
            stickySec: 'data-ad-sticky-sec', // number of seconds for sticky behaviour
        },
        classes: {
            highImpact: 'ad__high-impact'
        }
    }

    // tslint:disable-next-line: max-line-length
    // @see https://github.com/Postmedia-Digital/content.canada.com/blob/master/newspaper.com/common/js/gpt_wallpaper.min.js
    public static Wallpaper = {
        bodyClass: 'wallpaper',
        containerId: 'wallpaper-wrapper',
        readyId: 'wallpaper-spot-left',
        selector: '#wallpaper-wrapper a',
        size: [4, 4] // tslint:disable-line: no-magic-numbers
    }

    public static Native = {
        size: [5, 5] // tslint:disable-line: no-magic-numbers
    }

    public static Interscroller = {
        sizes: [[6, 6], [7, 7]] // tslint:disable-line: no-magic-numbers
    }

    protected adLight = false
    protected refreshAd: boolean | IAdRefreshConf
    protected adSticky: typeof Ad
    protected optOutPoliticalAds = true
    protected registered = false
    protected pageAgeGated = false
    protected pageType = ''

    // Verifier platforms

    /**
     * Stores adapter, formatter, targeter refs.
     */
    public constructor(
        protected gpt: IGptAdapter,
        protected formatter: AdURIFormatter,
        protected targeting: IAdTargetStrategyHandler) {
        super()
        this.synchronizeStores()
    }

    /**
     * Removes content rendered outside ad containers. Currently just CCN wallpapers.
     */
    public static destroyOOP(ads: IAd[]) {
        // CCN wallpaper
        ads
            .filter((ad) => ad.sizeRendered && ad.isWallpaper())
            .forEach(() => {
                document.body.classList.remove(AdService.Wallpaper.bodyClass)
                const el = document.getElementById(AdService.Wallpaper.containerId)
                if (el) {
                    el.remove()
                }
            })
    }

    /**
     * remove special characters from ads configure targeting.
     */
    public static removeSpecialCharacters(data: string[]) {
        return data !== undefined ? data.map((_: string) => _.replace(/[!'()*]/g, '')) : []
    }

    private static getTargetURI(targets: AdTarget | AdTarget[]): string {
        return encodeURIComponent([].concat(targets).map(
            (target: { key: string, value: string | string[] }) => {
                return target.key + '=' + target.value
            }).join('&'))

    }

    /**
     * Registers ads in DOM via primary attribute selector (slot), configure targeting.
     */
    public async initialize(conf: IAdServiceConfig) {

        logger('debug', `${TimerLabels.PAGE_LOAD} - Ads initializing`)
        loggerTimeLog(TimerLabels.PAGE_LOAD)

        this.gpt.adURI = this.formatter(
            conf.provider.account,
            conf.siteId,
            AdService.removeSpecialCharacters(conf.ancestry),
            conf.page
        )

        if (conf.pageConfig && conf.pageConfig.ageGate && conf.pageConfig.ageGate.enabled) {
            this.pageAgeGated = true
        }

        this.gpt.registerVendors()

        this.pageType = conf.page
        const drivingTaxonomy: IDrivingConfiguration = (conf.pageConfig && conf.pageConfig.drivingTaxonomy) || {}

        // configure page level targeting (applies to all ads on a page)
        this.gpt.target(
            this.targeting.page({
                ancestry: conf.ancestry,
                articleId: conf.articleId,
                bodyType: drivingTaxonomy.bodyType,
                carMake: drivingTaxonomy.carMake,
                carModel: drivingTaxonomy.carModel,
                modelYears: drivingTaxonomy.modelYears,
                negative: conf.negative,
                optOutPoliticalAds: this.optOutPoliticalAds,
                originId: conf.originId,
                property: conf.property,
                sensitive: conf.sensitive,
                specialSection: drivingTaxonomy.specialSection,
                type: conf.page,
                vehicleType: drivingTaxonomy.vehicleType,
                wpLegacyId: conf.wpLegacyId,
                writers: conf.writers
            }))

        this.setRefreshAd(conf.refresh)
        await this.update(conf.adLight)
        await this.registerNewAdsEventListener()

    }

    /**
     * Returns Ad Targeting attributes as a URI for the given configuration.
     */
    public async getTargeting(conf: IAdServiceConfig): Promise<IAdTargetingConfig> {
        let iasScores: IIasScores = {}
        let iasScoresFiltered: object = {}
        try {
            iasScores = JSON.parse(await this.gpt.getIasScores())
            iasScoresFiltered = {...iasScores.brandSafety, ...iasScores.custom, ...{fr: iasScores.fr}}

        } catch (error) {
            logger('debug', `video ad targeting error IAS config - getTargeting async : ${error}`)
        }

        const adUnit = this.formatter(
            conf.provider.account,
            conf.siteId,
            AdService.removeSpecialCharacters(conf.ancestry),
            conf.page
        )

        const drivingTaxonomy: IDrivingConfiguration = (conf.pageConfig && conf.pageConfig.drivingTaxonomy) || {}

        const adTargetingParams = AdService.getTargetURI(this.targeting.page({
            ancestry: conf.ancestry,
            articleId: conf.articleId,
            bodyType: drivingTaxonomy.bodyType,
            carMake: drivingTaxonomy.carMake,
            carModel: drivingTaxonomy.carModel,
            modelYears: drivingTaxonomy.modelYears,
            negative: conf.negative,
            optOutPoliticalAds: this.optOutPoliticalAds,
            originId: conf.originId,
            property: conf.property,
            sensitive: conf.sensitive,
            specialSection: drivingTaxonomy.specialSection,
            type: conf.page,
            vehicleType: drivingTaxonomy.vehicleType,
            writers: conf.writers
        }, iasScoresFiltered))

        return {adUnit, adTargetingParams}

    }

    public setRefreshAd(refresh: boolean | IAdRefreshConf): void {
        this.refreshAd = refresh
    }

    public async update(adLight: boolean) {
        logger('debug', `${TimerLabels.PAGE_LOAD} - Ads refreshed - adlight is ${adLight}`)
        loggerTimeLog(TimerLabels.PAGE_LOAD)
        this.adLight = adLight

        // if no ad-light and ads already registered then nothing to do
        if (!this.adLight && this.registered) {
            return
        }

        // if page overlay age gate is enabled, wait to register
        // ads until user passes through age gate
        if (this.pageAgeGated) {
            await PageAgeGateService.verified
        }

        logger('info', `update->registerAds()`)
        await this.registerAds()
    }

    public async registerNewAdsEventListener() {
        addEventListener(AdEvents.SLOT_ADDED, async (e: IAdSlotsAddedEvent) => {
            if (e.detail.el) {
                this.registered = false
                const ad = e.detail.el.map((el: any) => {
                    return new Ad(
                        el, this.targeting.ad.bind(this.targeting), this.refreshAd, this.adLight, this.pageType)
                })
                logger('info', `registerNewAdsEventListener->registerAds()`)
                await this.registerAds(ad)
            }
        })
    }

    /**
     * Retrieve all ads from DOM.
     */
    protected getAds(): IAd[] {
        return Ad.getAdElements()
            .map((el: Element) => {
                const ad = el.hasAttribute(AdService.DOM.attrs.sticky) ? this.adSticky : Ad
                return new ad(el, this.targeting.ad.bind(this.targeting), this.refreshAd, this.adLight, this.pageType)
            })
            .filter((ad) => !this.adLight || ad.isAdLightIneligible())
        // Filter for ineligible ads for adlight, e.g. native ads
    }

    /**
     * Registers ads with service adapter or displays subscriber messaging.
     */
    protected async registerAds(specificAds?: IAd[]) {
        await DOMUtils.ready

        logger('debug', `${TimerLabels.PAGE_LOAD} - registerAds called`)
        loggerTimeLog(TimerLabels.PAGE_LOAD)

        if (this.adLight) {
            // do not register incoming specific ads (e.g. Viafoura)
            if (specificAds) {
                this.registered = true
                return

            }
            // remove registered ads
            if (this.registered) {
                AdService.destroyOOP(this.gpt.ads)
                // remove all ads except native
                this.gpt.ads = this.gpt.ads.filter((ad) => ad.isAdLightIneligible())
                this.registered = false
            }
        }

        // already registered
        if (this.registered) {
            return
        }
        this.registered = true

        // has sticky ad(s), load mixin
        if (!this.adSticky && document.querySelector(`[${AdService.DOM.attrs.sticky}]`)) {
            const {WithSticky} = await import('./ad/sticky')
            this.adSticky = WithSticky(Ad)
        }

        // Store non-VF ads
        if (!specificAds) {
            this.getAds().forEach((ad) => this.gpt.storeAd(ad))
        }

        await this.gpt.initRequestAds(specificAds)

        this.gpt.repopulateAds()

        // request
        if (specificAds) {
            logger('info', `AdService requestAds with specificAds = ${specificAds.length}`)
        } else {
            logger('info', `AdService requestAds with specificAds empty`)
        }
    }

    /**
     * Synchronizes global>local settings (currently just political ad opt out).
     */
    private synchronizeStores() {

        // get local political ad opt out
        const localOptOut = LocalStore.get(Store.OPT_OUT_POL)

        // default to true
        this.optOutPoliticalAds = localOptOut === undefined ? true : !!localOptOut

        // begin fetch of political ad opt out from global storage
        GlobalStore.get(Store.OPT_OUT_POL)
            .then((val) => {
                this.optOutPoliticalAds = !!val

                // sync to local store, even if falsy (in order to determine if set)
                if (this.optOutPoliticalAds !== localOptOut) {
                    LocalStore.set(Store.OPT_OUT_POL, this.optOutPoliticalAds)
                }
            })
            .catch(console.log)
    }
}
